// Travel service - API integration with localStorage cache

import api from '../utils/api.js';

const STORAGE_KEY = 'travel:state';
const BASE_SPEED_KMH = 60; // Base speed: 60 km/h

// Default state structure
const defaultState = {
  activeTravel: null, // { runId, fromCityId, toCityId, startedAt, endsAt, distance }
  travelHistory: [], // Array of completed travels
  currentLocation: 'town' // Player's current city
};

class TravelService {
  constructor() {
    this.state = this.loadState();
  }

  // Load state from localStorage
  loadState() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          ...defaultState,
          ...parsed
        };
      }
    } catch (error) {
      console.error('Failed to load travel state:', error);
    }
    return { ...defaultState };
  }

  // Save state to localStorage
  saveState(suppressEvent = false) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
      // Emit global event for state synchronization (unless suppressed)
      if (!suppressEvent) {
        this.emitStateChanged();
      }
    } catch (error) {
      console.error('Failed to save travel state:', error);
    }
  }

  // Emit global state change event
  emitStateChanged(source = 'service') {
    try {
      // Add debouncing to prevent rapid successive events
      if (this._emitTimeout) {
        clearTimeout(this._emitTimeout);
      }
      
      this._emitTimeout = setTimeout(() => {
        window.dispatchEvent(new CustomEvent('travel:state-changed', {
          detail: {
            activeTravel: this.state.activeTravel,
            currentLocation: this.state.currentLocation,
            traveling: !!this.state.activeTravel,
            timestamp: Date.now(),
            source: source // Identify event source to prevent loops
          }
        }));
      }, 50); // 50ms debounce
    } catch (error) {
      console.warn('Failed to emit travel state change event:', error);
    }
  }

  // Get all cities from API
  async getCities() {
    try {
      const response = await api.getCities();
      this._cachedCities = response.cities || [];
      return this._cachedCities;
    } catch (error) {
      console.error('Failed to fetch cities:', error);
      
      // Return cached cities if available
      if (this._cachedCities) {
        console.warn('Using cached cities data');
        return this._cachedCities;
      }
      
      throw error;
    }
  }

  // Get city data by ID (with cache and guards)
  getCityData(cityId) {
    if (!cityId) return null;
    
    // Return from cached cities if available
    if (this._cachedCities) {
      const city = this._cachedCities.find(c => c.id === cityId);
      if (city) return city;
    }
    
    // Fallback for common cities
    const commonCities = {
      'town': {
        id: 'town',
        name: 'Ciudad Principal',
        description: 'El centro de actividad comercial',
        biome: 'plains',
        coordinates: { x: 0, y: 0 }
      },
      'forest': {
        id: 'forest',
        name: 'Bosque Verde',
        description: 'Un denso bosque lleno de recursos',
        biome: 'forest',
        coordinates: { x: 50, y: 30 }
      }
    };
    
    return commonCities[cityId] || null;
  }

  // Get available destinations from API
  async getAvailableDestinations() {
    try {
      const cities = await this.getCities();
      // Only emit state change if we actually got new data
      return cities;
    } catch (error) {
      console.error('Failed to fetch destinations:', error);
      return [];
    }
  }

  // Calculate distance between two cities
  calculateDistance(fromCityId, toCityId) {
    const fromCity = this.getCityData(fromCityId);
    const toCity = this.getCityData(toCityId);
    
    if (!fromCity || !toCity) {
      console.warn(`Cities not found: ${fromCityId} -> ${toCityId}`);
      return 50; // Default distance
    }

    // Use stored distance or calculate from coordinates
    if (toCity.distanceKm) {
      return toCity.distanceKm;
    }

    // Fallback: calculate Euclidean distance if coordinates exist
    if (toCity.coordinates && fromCity.coordinates) {
      const dx = toCity.coordinates.x - fromCity.coordinates.x;
      const dy = toCity.coordinates.y - fromCity.coordinates.y;
      return Math.sqrt(dx * dx + dy * dy);
    }
    
    return 50; // Default fallback distance
  }

  // Calculate travel time with player stats
  calculateETA(fromCityId, toCityId) {
    const distance = this.calculateDistance(fromCityId, toCityId);
    const toCity = this.getCityData(toCityId);
    
    // Base time in minutes
    let baseMinutes = Math.ceil((distance / BASE_SPEED_KMH) * 60);
    
    // Apply biome speed modifier if city and biome data available
    if (toCity && toCity.biome) {
      const biomes = {
        'plains': { speedModifier: 1.0 },
        'forest': { speedModifier: 0.8 },
        'mountain': { speedModifier: 0.6 },
        'desert': { speedModifier: 0.7 },
        'coastal': { speedModifier: 1.1 }
      };
      
      const biome = biomes[toCity.biome];
      if (biome && biome.speedModifier) {
        baseMinutes = Math.ceil(baseMinutes / biome.speedModifier);
      }
    }

    // Apply player agility bonus (if available)
    const playerStore = window.Alpine?.store('app');
    if (playerStore && playerStore.player && playerStore.player.agility) {
      const agilityBonus = Math.min(0.03 * playerStore.player.agility, 0.3); // Max 30% reduction
      baseMinutes = Math.ceil(baseMinutes * (1 - agilityBonus));
    }

    return Math.max(1, baseMinutes); // Minimum 1 minute
  }

  // Preview travel details without starting
  async preview(toCityId) {
    try {
      const response = await api.getTravelPreview(toCityId);
      const preview = response.preview;
      
      return {
        fromCity: preview.from,
        toCity: preview.to,
        cost: preview.cost,
        duration: preview.duration,
        canAfford: preview.canAfford,
        currentCoins: preview.currentCoins
      };
    } catch (error) {
      console.error('Failed to preview travel:', error);
      throw new Error(error.data?.message || 'No se pudo obtener la vista previa del viaje');
    }
  }

  // Start travel
  async start(toCityId) {
    try {
      const response = await api.startTravel(toCityId);
      const travel = response.travel;
      
      // Create local state for timers
      const activeTravel = {
        runId: travel.id,
        fromCityId: travel.from.id,
        toCityId: travel.to.id,
        fromCity: travel.from,
        toCity: travel.to,
        cost: travel.cost,
        duration: travel.duration,
        startedAt: new Date(travel.startedAt).getTime(),
        endsAt: new Date(travel.arrivesAt).getTime(),
        status: 'traveling'
      };

      this.state.activeTravel = activeTravel;
      this.state.currentLocation = null; // Traveling
      this.saveState();

      // Set timer in game store
      const playerStore = window.Alpine?.store('app');
      if (playerStore) {
        playerStore.setTimer('travel', activeTravel.endsAt, () => {
          this.completeTravel(activeTravel.runId);
        });
        
        // Refresh player data to reflect new coin amount
        await playerStore.refreshPlayer();
      }

      console.log(`Travel started: ${travel.from.name} -> ${travel.to.name} (${activeTravel.runId})`);
      return activeTravel;
      
    } catch (error) {
      console.error('Failed to start travel:', error);
      throw new Error(error.data?.message || 'No se pudo iniciar el viaje');
    }
  }

  // Get active travel
  async getActive() {
    try {
      const response = await api.getTravelStatus();
      
      if (!response.travel) {
        // No active travel on server, clear local state
        if (this.state.activeTravel) {
          this.state.activeTravel = null;
          this.saveState();
        }
        return null;
      }

      const travel = response.travel;
      
      // Update local state with server data
      const activeTravel = {
        runId: travel.id,
        fromCityId: travel.from.id,
        toCityId: travel.to.id,
        fromCity: travel.from,
        toCity: travel.to,
        cost: travel.cost,
        duration: travel.duration,
        startedAt: new Date(travel.startedAt).getTime(),
        endsAt: new Date(travel.arrivesAt).getTime(),
        status: travel.isComplete ? 'completed' : 'traveling'
      };

      // Only save and emit if state actually changed
      const stateChanged = !this.state.activeTravel || 
        this.state.activeTravel.runId !== activeTravel.runId ||
        this.state.activeTravel.status !== activeTravel.status;

      this.state.activeTravel = activeTravel;
      
      if (stateChanged) {
        this.saveState();
      }

      // Set timer if travel is still active
      if (!travel.isComplete) {
        const playerStore = window.Alpine?.store('app');
        if (playerStore) {
          playerStore.setTimer('travel', activeTravel.endsAt, () => {
            this.completeTravel(activeTravel.runId);
          });
        }
      }

      return activeTravel;
      
    } catch (error) {
      console.error('Failed to fetch travel status:', error);
      
      // Return cached state if available
      if (this.state.activeTravel) {
        console.warn('Using cached travel data');
        return this.state.activeTravel;
      }
      
      return null;
    }
  }

  // Complete travel if time has elapsed
  completeIfElapsed() {
    if (!this.state.activeTravel) return false;

    const now = Date.now();
    if (this.state.activeTravel.endsAt <= now && this.state.activeTravel.status === 'traveling') {
      this.completeTravel(this.state.activeTravel.runId);
      return true;
    }
    return false;
  }

  // Complete travel
  async completeTravel(runId) {
    try {
      const response = await api.completeTravel();
      
      // Update current location
      this.state.currentLocation = response.player.cityId;
      
      // Mark as completed and move to history
      const completedTravel = {
        ...this.state.activeTravel,
        status: 'completed',
        completedAt: Date.now()
      };
      
      this.state.travelHistory.push(completedTravel);
      this.state.activeTravel = null;

      // Limit travel history
      if (this.state.travelHistory.length > 20) {
        this.state.travelHistory = this.state.travelHistory.slice(-20);
      }

      this.saveState();

      // Clear timer and update player store
      const playerStore = window.Alpine?.store('app');
      if (playerStore) {
        playerStore.clearTimer('travel');
        
        // Refresh player data to get new location
        await playerStore.refreshPlayer();
        
        // Show arrival notification
        playerStore.addNotification(
          response.message, 
          'success'
        );
      }

      console.log(`Travel completed: ${response.message}`);
      return completedTravel;
      
    } catch (error) {
      console.error('Failed to complete travel:', error);
      
      // Fallback: just clear the timer and local state
      const playerStore = window.Alpine?.store('app');
      if (playerStore) {
        playerStore.clearTimer('travel');
      }
      
      this.state.activeTravel = null;
      this.saveState();
    }
  }

  // Cancel travel
  cancel() {
    if (!this.state.activeTravel) {
      throw new Error('No hay viaje activo para cancelar.');
    }

    // Refund partial cost (50%)
    const refund = Math.floor(this.state.activeTravel.cost * 0.5);
    const playerStore = window.Alpine?.store('app');
    
    if (playerStore && refund > 0) {
      playerStore.updatePlayer({
        money: playerStore.player.money + refund
      });
      playerStore.addNotification(`Viaje cancelado. Reembolso: ${refund} monedas`, 'info');
    }

    // Clear travel state
    this.state.activeTravel = null;
    this.saveState();

    // Clear timer
    if (playerStore) {
      playerStore.clearTimer('travel');
    }

    console.log('Travel cancelled');
  }

  // Clear all travel state
  clear() {
    const playerStore = window.Alpine?.store('app');
    if (playerStore) {
      playerStore.clearTimer('travel');
    }

    this.state.activeTravel = null;
    this.saveState();
  }

  // Helper methods
  generateRunId() {
    return 'travel_' + Date.now() + '_' + Math.random().toString(36).substring(2);
  }

  calculateTravelCost(distance) {
    // Base cost: 1 coin per 10km
    return Math.ceil(distance / 10);
  }

  getRouteInfo(fromCityId, toCityId) {
    // Get route information if available
    const routeKey = `${fromCityId}_${toCityId}`;
    const reverseRouteKey = `${toCityId}_${fromCityId}`;
    
    return citiesData.routes[routeKey] || citiesData.routes[reverseRouteKey] || null;
  }

  getCurrentLocation() {
    return this.state.currentLocation;
  }

  setCurrentLocation(cityId) {
    if (!this.getCityData(cityId)) {
      throw new Error(`Ciudad ${cityId} no encontrada.`);
    }
    
    this.state.currentLocation = cityId;
    this.saveState();
  }

  // Get travel statistics
  getStats() {
    const totalTravels = this.state.travelHistory.length;
    const totalDistance = this.state.travelHistory.reduce((sum, travel) => 
      sum + travel.distance, 0
    );
    const totalCost = this.state.travelHistory.reduce((sum, travel) => 
      sum + travel.cost, 0
    );

    return {
      totalTravels,
      totalDistance: Math.round(totalDistance),
      totalCost,
      averageDistance: totalTravels > 0 ? Math.round(totalDistance / totalTravels) : 0,
      citiesVisited: [...new Set(this.state.travelHistory.map(t => t.toCityId))].length
    };
  }

  // Reset all travel data
  reset() {
    this.state = { ...defaultState };
    this.saveState();
    
    const playerStore = window.Alpine?.store('app');
    if (playerStore) {
      playerStore.clearTimer('travel');
    }
    
    console.log('Travel data reset');
  }
}

// Export singleton instance
export const travelService = new TravelService();

// Export class for testing
export { TravelService };